let Nuxeo = require('nuxeo/es5');
import {modelStates} from './constants';
import moment from 'moment';

class InsightService {
    constructor() {
        this._initialized = false;
    }

    init(url, token, projectId, username, password) {
        if (this._initialized) {
            return;
        }

        this._url = url;
        this._projectId = projectId;

        let options = {
            baseURL: url,
            schemas: ['*'],
        };

        if (token && token !== '') {
            options.auth = {
                method: 'token',
                token: token
            };
            options.headers = {'X-No-Basic-Header': true};
        } else {
            // using basic auth
            options.auth = {
                method: 'basic',
                username: username,
                password: password
            }
        }


        this._client = new Nuxeo(options);

        this._initialized = true;
    }

    connect() {
        if (!this._initialized) {
            throw 'Not initialized';
        }
        if (this._client) {
            return this._client;
        }

        this._client._promise = this._client.connect();
        return this._client._promise.then(this._handleConnected.bind(this)).catch((error) => {
            console.log(error);
            if (error.response && error.response.status === 401) {
                throw 'Auth required: 401';
            }
            throw error;
        });
    }

    _handleConnected(nuxeo) {
        if (this._client.connected) {
            this._user = nuxeo.user;
            this._nuxeoVersion = nuxeo.nuxeoVersion;
        }
        return nuxeo.user;
    }

    listModels(state) {
        let url = `${this._apiUrl()}/models`;
        if (state) {
            url += `?state=${state}`;
        }

        const options = this._buildOptions(url);
        return this._client.http(options);
    }

    listPublished() {
        let url = `${this._apiUrl()}/models?publishState=published`;
        const options = this._buildOptions(url);
        return this._client.http(options);
    }

    _apiUrl(url, projectId) {
        return `${url}/api/v1/ai/${projectId}`;
    }

    _buildOptions(url, props = '*') {
        return {
            method: 'GET',
            headers: {
                'X-NXproperties': props
            },
            url: url
        };
    }

    setManager(user, projectId, localStorage) {
        const managerGroup = projectId + "-managers";
        let isManager = user.properties.groups.includes(managerGroup) || user.properties.groups.includes("administrators");
        //isManager = true;
        localStorage.setItem("client-ismanager", isManager);
        return isManager;
    }

    setId(user, localStorage) {
        localStorage.setItem("client-username", user.properties.username);
    }

    static getId() {
        return localStorage.getItem("client-username");
    }

    static isManager() {
        return JSON.parse(localStorage.getItem("client-ismanager"));
    }

    listModelsPP(client, fulltext, aggregations, isVersion = true) {
        client.params = {
            system_fulltext: fulltext,
            queryParams: isVersion ? '("false", "true")' : '("false")',
        };
        Object.assign(client.params, aggregations);
        return client.fetch();
    }

    isTraining(model) {
        if (model) {
            const trainingStates = [modelStates.START, modelStates.FIRST_TRAINING_ERROR,
                modelStates.EVALUATION_ERROR, modelStates.NOT_TRAINED,
                modelStates.FIRST_TRAINING, modelStates.UPDATING, modelStates.TRAINING, modelStates.EVALUATING];
            return trainingStates.includes(model.state) && !this.isPublished(model);
        }
    }

    isRunning(model) {
        if (model) {
            const runningStates = [modelStates.FIRST_TRAINING, modelStates.UPDATING, modelStates.TRAINING, modelStates.EVALUATING];
            return runningStates.includes(model.state);
        }
    }

    isTrained(model) {
        if (model) {
            return modelStates.TRAINED === model.state;
        }
    }

    isLowPerformance(model) {
        if (model && model.properties) {
            const mainAccuracy = model.properties['ai_model:main_result'];
            return (mainAccuracy !== undefined && mainAccuracy !== null && mainAccuracy < 0.5) && model.state === modelStates.TRAINED;
        }
    }

    isPublished(model) {
        if (model && model.properties) {
            return model.properties['ai_model:deployment_information'].publishing_state === 'published';
        }
    }

    isFavorites(model) {
        if (model && model.contextParameters && model.contextParameters.favorites) {
            return model.contextParameters.favorites.isFavorite;
        }
        return false;
    }

    isLive(model) {
        return model && !model.isVersion;
    }

    getLiveModel(model) {
        if (model) {
            if (!model.isVersion) {
                return model;
            }
            if (model.contextParameters && model.contextParameters.versions
                && model.contextParameters.versions.entries && model.contextParameters.versions.entries.length !== 0) {
                return model.contextParameters.versions.entries.filter((model) => !model.isVersion)[0];
            }
        } else {
            return undefined;
        }
    }

    isNotif(model) {
        if (model && model.contextParameters && model.contextParameters.subscribedNotifications) {
            return model.contextParameters.subscribedNotifications.length > 0;
        }
        return false;
    }

    toggleFavorite(model, addFav, removeFav, displaySuccess, displayError, setModels, pageProvider, isSummary, displayLoading) {
        if (!this.isFavorites(model)) {
            addFav.execute().then(() => {
                displaySuccess('Added to favorites');
                if (!isSummary) {
                    this.fetchModels(pageProvider, setModels, displayError, displayLoading);
                }
            }).catch(error => {
                console.error(error);
                displayError('Cannot add to favorites');
            });
        } else {
            removeFav.execute().then(() => {
                displaySuccess('Removed from favorites');
                if (!isSummary) {
                    this.fetchModels(pageProvider, setModels, displayError, displayLoading);
                }
            }).catch(error => {
                console.error(error);
                displayError('Cannot remove from favorites');
            });
        }
    }

    toggleNotif(model, subscribe, unsubscribe, displaySuccess, displayError, setModels, pageProvider, isSummary, displayLoading) {
        if (!this.isNotif(model)) {
            subscribe.execute().then(() => {
                displaySuccess('Notification activated');
                if (!isSummary) {
                    this.fetchModels(pageProvider, setModels, displayError, displayLoading);
                }
            }).catch(error => {
                console.error(error);
                displayError('Cannot activate notification');
            });
        } else {
            unsubscribe.execute().then(() => {
                displaySuccess('Disable notification');
                if (!isSummary) {
                    this.fetchModels(pageProvider, setModels, displayError, displayLoading);
                }
            }).catch(error => {
                console.error(error);
                displayError('Cannot disable notification');
            });
        }
    }

    fetchModels(pageProvider, setModels, displayError, displayLoading) {
        displayLoading(true);
        insightService.listModelsPP(pageProvider).then((response) => {
            setModels(response.entries);
            displayLoading(false);
        }).catch((error) => {
            console.error(error);
            displayLoading(false);
            displayError('Cannot fetch models');
        });
    }

    getModelsBasedOnType(client, projectId, type = 'Document') {
        let url = `${this._apiUrl(client._baseURL, projectId)}/models/${type}`;
        const options = this._buildOptions(url);
        return client.http(options);
    }

    getAnnotationsForModel(client, projectId, missionId, type) {
        let url = `${client._baseURL}/api/v1/ai/missions/${projectId}/${missionId}/count/${type}`;
        const options = this._buildOptions(url);
        return client.http(options);
    }

    formatDate(date) {
        return moment(date).format('DD  MMM');
    }

    shortenDate(date) {
        return moment(date).format('MMM DD');
    }

    getMoment(date) {
        return date ? moment(date) : null;
    }

    isWithinLastWeek(date) {
        const aWeekOld = moment().subtract(7, 'days').startOf('day');
        return moment(date).isAfter(aWeekOld);
    }

    formatDateWithYear(date) {
        return moment(date).format('DD/MM/YYYY');
    }

    today() {
        return this.formatDate(moment());
    }

    getElapsedTime(startDate) {
        const ms = this.getMoment(moment()).diff(this.getMoment(startDate));
        const d = moment.duration(ms);
        return Math.floor(d.asHours()) + moment.utc(ms).format(":mm:ss");
    }

    fillTrainingMetrics(dataModel) {
        const jobInfo = dataModel.contextParameters.jobInfo;
        let result;
        if (jobInfo) {
            result = {
                progressSteps: jobInfo.guaranteedMetric,
                progressPending: jobInfo.bestMetric,
                progress: jobInfo.progress,
            }
            if (this.isRunning(dataModel) || this.isTrained(dataModel)) {
                const time = this.getElapsedTime(jobInfo.startDate);
                result.elapsedTime = `${time}`;
            }
        } else {
            result = {
                progressSteps: 0,
                progressPending: 0,
                progress: 0,
                elapsedTime: ''
            }
        }
        return result;
    }

    getPriorityIcon(priority) {
        switch (priority) {
            case 'high':
                return {
                    svg: 'high.svg',
                    color: 'rgb(235,194,72)',
                };
            case'critical':
                return {
                    svg: 'critical.svg',
                    color: 'red',
                };
            case'medium':
                return {
                    svg: 'medium.svg',
                    color: 'orange',
                };
            case'major':
                return {
                    svg: 'major.svg',
                    color: 'red',
                };
            case'low':
                return {
                    svg: 'low.svg',
                    color: 'blue',
                };
            case'minor':
                return {
                    svg: 'minor.svg',
                    color: 'blue',
                };
            default:
                return {
                    svg: 'trivial.svg',
                    color: 'blue',
                };
        }
    }

}

export const insightService = new InsightService();
export default InsightService;
